import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

export default function Services() {
  return (
    <>
      {/* SEO + Open Graph */}
      <Helmet>
        <title>Salon Software & Booking System for Salons | Maniera</title>
        <meta
          name="description"
          content="Custom salon software with booking system, appointment scheduling, and client management dashboard. Designed to help salons attract clients, reduce no-shows, and grow faster."
        />
        <link rel="canonical" href="https://www.manieradigital.com/services" />

        <meta property="og:title" content="Salon Software | Maniera" />
        <meta
          property="og:description"
          content="Custom salon software with booking system, scheduling, and client management."
        />
        <meta property="og:image" content="/public/img/services-hero.png" />
        <meta property="og:type" content="website" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Maniera Salon Software",
            provider: {
              "@type": "Organization",
              name: "ManieraDigital",
              url: "https://www.manieradigital.com"
            },
            description:
              "Custom salon software with booking system, appointment scheduling, and client management dashboard."
          })}
        </script>
      </Helmet>

      {/* HERO */}
      <section className="services-hero d-flex align-items-center">
        <Container>
          <Row>
            <Col lg={6}>
              <h1 className="services-hero-title">
                Salon Software & <span>Booking System</span> for Modern Salons
              </h1>
              <p className="services-hero-subtitle">
                Custom salon software with online booking system, appointment scheduling, and client management dashboard — designed to help your salon attract more clients, reduce missed appointments, and grow consistently.
              </p>
              <Link to="/salon-software-demo" className="hero-button mt-3">
                Explore Our Demo
              </Link>
            </Col>
          </Row>
        </Container>
      </section>

      {/* INTRO / FEATURES */}
      <section className="py-5 bg-light">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="services-title">The Maniera Salon System™</h2>
              <p className="services-subtitle">
                A complete salon software solution with booking system, client management, and SEO optimization — built to help modern salons automate appointments, improve client experience, and increase revenue.
              </p>
            </Col>
          </Row>

          <Row className="gy-4">
            {[
              { 
                title: "Online Booking System", 
                desc: "24/7 automated salon booking system with real-time appointment scheduling, confirmations, and reminders to reduce missed bookings and increase client satisfaction." 
              },
              { 
                title: "Client Management Dashboard", 
                desc: "Manage clients, appointments, and communication in one place with a powerful salon management system designed for efficiency." 
              },
              { 
                title: "SEO & Google Visibility", 
                desc: "Rank your salon on Google and attract new clients searching for hair salons, barbershops, and beauty services in your area." 
              },
              { 
                title: "Custom Salon Website Design", 
                desc: "Premium, mobile-friendly salon website designed to convert visitors into booked clients while reflecting your brand identity." 
              }
            ].map((feature, idx) => (
              <Col md={6} lg={3} key={idx}>
                <div className="service-card">
                  <h3>{feature.title}</h3>
                  <p>{feature.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-5">
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <h2 className="services-title">Designed for Ambitious Salons</h2>
              <ul className="services-list">
                <li>Premium hair salons & modern barbershops</li>
                <li>Owners who want more bookings and fewer no-shows</li>
                <li>Businesses ready to automate appointments</li>
                <li>Professionals looking to grow through SEO and online visibility</li>
              </ul>

              <p className="services-note">
                Perfect for salons looking for a booking system, client management software, and a strong online presence that attracts new clients through Google.
              </p>

              <p>
                Experience the system —{" "}
                <Link to="/salon-software-demo" className="premium-link">
                  explore our salon software demo
                </Link>{" "}
                and see how it improves bookings and workflow.
              </p>
            </Col>

            <Col lg={6}>
              <div className="services-image"></div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* PROCESS */}
      <section className="py-5 bg-light">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="services-title">How We Work</h2>
              <p className="services-subtitle">
                A structured process for building your custom salon software — from strategy and design to development, launch, and SEO optimization.
              </p>
            </Col>
          </Row>

          <Row className="text-center">
            {[
              "Strategy & Consultation",
              "Design & Brand Direction",
              "System Development",
              "Launch & Optimization"
            ].map((step, idx) => (
              <Col md={3} key={idx}>
                <div className="process-step">{`0${idx + 1}`}</div>
                <p>{step}</p>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* SEO BLOCK (VERY IMPORTANT) */}
      <section className="py-5">
        <Container>
          <Row className="text-center">
            <Col>
              <h2 className="services-title">
                Salon Software with Booking System & Client Management
              </h2>
              <p className="services-subtitle">
                ManieraDigital provides a complete salon software platform including online booking system, appointment scheduling, and client management tools. 
                Our solution helps salons reduce missed appointments, automate daily operations, and attract new clients through SEO optimization and modern web design.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA */}
      <section className="services-cta text-center">
        <Container>
          <h2>Ready to Grow Your Salon?</h2>
          <p>
            Get a custom salon software solution with booking system, appointment scheduling, and client management — built to grow your salon and automate your business 24/7.
          </p>
          <Link to="/contact" className="hero-button mt-3">
            Book Free Consultation
          </Link>
        </Container>
      </section>
    </>
  );
}